/**
 * RD & Company CRM - Client Profile Layout
 * Displays all clients with their detailed information
 */

import { useState, useEffect } from "react";

// @mui material components
import {
  Card,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Menu,
  MenuItem,
  Icon,
} from "@mui/material";

// @mui icons
import {
  Visibility,
  Phone,
  Email,
  LocationOn,
  Business,
  Receipt,
  Person,
} from "@mui/icons-material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";

// API Configuration
import { API_CONFIG, getApiUrl } from "config/api";
import { useAuth } from "contexts/AuthContext";

function ClientProfile() {
  const { token } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [menu, setMenu] = useState(null);

  const fetchClients = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.USERS), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error (${response.status}): ${errorText}`);
      }

      const data = await response.json();

      if (data.success) {
        // Filter only clients from all users
        const clientUsers = data.users.filter((user) => user.role === "client");
        setClients(clientUsers);
      } else {
        setError(data.message || "Failed to fetch clients");
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      setError(
        `Network error: ${err.message}. Please check if the backend server is running on http://127.0.0.1:8000`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, [token]);

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    setIsDetailOpen(true);
  };

  const handleCloseDetails = () => {
    setSelectedClient(null);
    setIsDetailOpen(false);
  };

  const openMenu = (event) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.segment?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { Header: "Client", accessor: "client", width: "25%" },
    { Header: "Contact", accessor: "contact", width: "20%" },
    { Header: "Location", accessor: "location", width: "20%" },
    { Header: "Segment", accessor: "segment", width: "15%" },
    { Header: "GST Number", accessor: "gst", width: "15%" },
    { Header: "Actions", accessor: "actions", width: "5%", disableSortBy: true },
  ];

  const rows = filteredClients.map((client) => ({
    client: (
      <Box>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {client.name}
        </MDTypography>
      </Box>
    ),
    contact: (
      <Box>
        {client.email && (
          <MDBox display="flex" alignItems="center" mb={0.5}>
            <Email fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {client.email}
            </MDTypography>
          </MDBox>
        )}
        {client.alternative_phone && (
          <MDBox display="flex" alignItems="center">
            <Phone fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {client.alternative_phone}
            </MDTypography>
          </MDBox>
        )}
        {!client.email && !client.alternative_phone && (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            No contact info
          </MDTypography>
        )}
      </Box>
    ),
    location: (
      <Box>
        {client.city && (
          <MDBox display="flex" alignItems="center" mb={0.5}>
            <LocationOn fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {client.city}
            </MDTypography>
          </MDBox>
        )}
        {client.address && (
          <MDTypography
            variant="caption"
            color="text"
            fontWeight="medium"
            sx={{ fontSize: "0.7rem" }}
          >
            {client.address.length > 40 ? `${client.address.substring(0, 40)}...` : client.address}
          </MDTypography>
        )}
        {!client.city && !client.address && (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            No location info
          </MDTypography>
        )}
      </Box>
    ),
    segment: (
      <Box>
        {client.segment ? (
          <MDBox display="flex" alignItems="center">
            <Business fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {client.segment}
            </MDTypography>
          </MDBox>
        ) : (
          <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
            Not provided
          </MDTypography>
        )}
      </Box>
    ),
    gst: (
      <Box>
        {client.gst_no ? (
          <MDBox display="flex" alignItems="center">
            <Receipt fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
            <MDTypography
              variant="caption"
              color="text"
              fontWeight="medium"
              sx={{ fontSize: "0.7rem", fontFamily: "monospace" }}
            >
              {client.gst_no}
            </MDTypography>
          </MDBox>
        ) : (
          <MDTypography variant="caption" color="text.secondary" fontWeight="medium">
            Not provided
          </MDTypography>
        )}
      </Box>
    ),
    actions: (
      <MDBox display="flex" alignItems="center" mt={{ xs: 2, sm: 0 }} ml={{ xs: -1.5, sm: 0 }}>
        <MDBox mr={1}>
          <MDButton variant="text" color="info" onClick={() => handleViewDetails(client)}>
            <Icon>visibility</Icon>&nbsp;view
          </MDButton>
        </MDBox>
        <MDButton variant="text" color="dark" onClick={openMenu}>
          <Icon>more_vert</Icon>
        </MDButton>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <MDTypography variant="h6" fontWeight="medium">
                  Client Profile
                </MDTypography>
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  placeholder="Search clients by name, email, city, or segment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  fullWidth
                />
              </MDBox>
            </MDBox>
          </Card>
        </MDBox>

        <Card>
          <MDBox pt={3} px={3}>
            <MDTypography variant="h6" fontWeight="medium">
              Clients List
            </MDTypography>
          </MDBox>
          <MDBox pt={1} pb={2} px={3}>
            {loading ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDTypography variant="body2" color="text">
                  Loading clients...
                </MDTypography>
              </MDBox>
            ) : error ? (
              <MDBox display="flex" justifyContent="center" py={4}>
                <MDTypography variant="body2" color="error">
                  {error}
                </MDTypography>
                <MDButton variant="gradient" color="info" onClick={fetchClients} sx={{ mt: 2 }}>
                  Retry
                </MDButton>
              </MDBox>
            ) : (
              <DataTable
                table={{ columns, rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                noEndBorder
              />
            )}
          </MDBox>
        </Card>
      </MDBox>

      <Menu
        id="simple-menu"
        anchorEl={menu}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        open={Boolean(menu)}
        onClose={closeMenu}
      >
        <MenuItem onClick={closeMenu}>View Details</MenuItem>
        <MenuItem onClick={closeMenu}>Edit Client</MenuItem>
        <MenuItem onClick={closeMenu}>Contact Client</MenuItem>
      </Menu>

      {/* Client Details Dialog */}
      <Dialog open={isDetailOpen} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Person sx={{ mr: 1 }} />
            Client Details
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="h6" gutterBottom>
                    Personal Information
                  </MDTypography>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Name:</strong> {selectedClient.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Email:</strong> {selectedClient.email || "Not provided"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Phone:</strong> {selectedClient.alternative_phone || "Not provided"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Role:</strong> {selectedClient.role}
                    </Typography>
                  </Box>
                </MDBox>
              </Grid>

              <Grid item xs={12} md={6}>
                <MDBox mb={2}>
                  <MDTypography variant="h6" gutterBottom>
                    Business Information
                  </MDTypography>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Segment:</strong> {selectedClient.segment || "Not provided"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>GST Number:</strong> {selectedClient.gst_no || "Not provided"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>City:</strong> {selectedClient.city || "Not provided"}
                    </Typography>
                  </Box>
                </MDBox>
              </Grid>

              <Grid item xs={12}>
                <MDBox mb={2}>
                  <MDTypography variant="h6" gutterBottom>
                    Address
                  </MDTypography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedClient.address || "Not provided"}
                  </Typography>
                </MDBox>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDetails} color="secondary">
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ClientProfile;
